import React, { useState, useEffect } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { 
  getProductById, getServiceById, getTrainingByIdCMS, 
  getEventById, getPartnerById, getTeamMemberById, getWhyUsById 
} from '../services/api';
import toast from 'react-hot-toast';

const AllDetails = () => {
  const { type, id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [item, setItem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [showFullImage, setShowFullImage] = useState(false);

  useEffect(() => {
    if (location.state?.item) {
      setItem(location.state.item);
      setLoading(false);
    } else if (id && type) {
      fetchItemDetails();
    } else {
      navigate('/resources');
    }
  }, [id, type, location.state]);

  const fetchItemDetails = async () => {
    setLoading(true);
    try {
      let response;
      switch(type) {
        case 'products':
          response = await getProductById(id);
          break;
        case 'services':
          response = await getServiceById(id);
          break;
        case 'trainings':
          response = await getTrainingByIdCMS(id);
          break;
        case 'events':
          response = await getEventById(id);
          break;
        case 'partners':
          response = await getPartnerById(id);
          break;
        case 'team':
          response = await getTeamMemberById(id);
          break;
        case 'whyus':
          response = await getWhyUsById(id);
          break;
        default:
          throw new Error('Invalid resource type');
      }
      setItem(response.data || response);
    } catch (error) {
      console.error('Error fetching details:', error);
      toast.error('Failed to load details');
      navigate('/resources');
    } finally {
      setLoading(false);
    }
  };

  const getFullImageUrl = (url) => {
    if (!url) return '';
    if (url.startsWith('http')) return url;
    if (url.startsWith('/uploads')) return `https://inteliheads.in${url}`;
    return url;
  };

  const getTypeConfig = () => {
    const configs = {
      products: { 
        title: 'Product Details', 
        icon: '📦', 
        color: '#2C7A7B',
        gradient: 'linear-gradient(135deg, #2C7A7B 0%, #236B6C 100%)',
        sections: ['overview', 'features', 'application']
      },
      services: { 
        title: 'Service Details', 
        icon: '⚙️', 
        color: '#3498db',
        gradient: 'linear-gradient(135deg, #3498db 0%, #2980b9 100%)',
        sections: ['overview', 'features', 'pricing']
      },
      trainings: { 
        title: 'Training Details', 
        icon: '🎓', 
        color: '#F5A623',
        gradient: 'linear-gradient(135deg, #F5A623 0%, #E67E22 100%)',
        sections: ['overview', 'curriculum', 'instructor']
      },
      events: { 
        title: 'Event Details', 
        icon: '📅', 
        color: '#9b59b6',
        gradient: 'linear-gradient(135deg, #9b59b6 0%, #8e44ad 100%)',
        sections: ['overview', 'agenda', 'speakers']
      },
      partners: { 
        title: 'Partner Details', 
        icon: '🤝', 
        color: '#1abc9c',
        gradient: 'linear-gradient(135deg, #1abc9c 0%, #16a085 100%)',
        sections: ['overview', 'contact']
      },
      team: { 
        title: 'Team Member Details', 
        icon: '👥', 
        color: '#e74c3c',
        gradient: 'linear-gradient(135deg, #e74c3c 0%, #c0392b 100%)',
        sections: ['overview', 'contact']
      },
      whyus: { 
        title: 'Why Us Details', 
        icon: '⭐', 
        color: '#f39c12',
        gradient: 'linear-gradient(135deg, #f39c12 0%, #e67e22 100%)',
        sections: ['overview', 'points']
      }
    };
    return configs[type] || { 
      title: 'Details', 
      icon: '📄', 
      color: '#2C7A7B',
      gradient: 'linear-gradient(135deg, #2C7A7B 0%, #236B6C 100%)',
      sections: ['overview'] 
    };
  };

  const handleInquiry = () => {
    window.dispatchEvent(new CustomEvent('openContactModal', { 
      detail: { item: item?.name || item?.title }
    }));
  };

  const handleBack = () => {
    navigate('/resources');
  };

  const typeConfig = getTypeConfig();

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#F7F9FC' }}>
        <div style={{ textAlign: 'center' }}>
          <div className="spinner" style={{
            width: '50px',
            height: '50px',
            border: '4px solid #E2E8F0',
            borderTopColor: typeConfig.color,
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            margin: '0 auto 1rem'
          }} />
          <p style={{ color: '#5A6E7F' }}>Loading details...</p>
          <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        </div>
      </div>
    );
  }

  if (!item) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', background: '#F7F9FC' }}>
        <div style={{
          background: 'white',
          padding: '3rem',
          borderRadius: '24px',
          textAlign: 'center',
          maxWidth: '400px',
          boxShadow: '0 20px 40px rgba(0,0,0,0.1)'
        }}>
          <i className="fas fa-exclamation-triangle" style={{ fontSize: '4rem', color: '#F5A623', marginBottom: '1rem' }}></i>
          <h3 style={{ color: '#0A192F', marginBottom: '0.5rem' }}>Item Not Found</h3>
          <p style={{ color: '#5A6E7F', marginBottom: '1.5rem' }}>The requested item could not be found.</p>
          <button onClick={handleBack} style={{
            padding: '0.75rem 1.5rem',
            background: '#2C7A7B',
            color: 'white',
            border: 'none',
            borderRadius: '12px',
            cursor: 'pointer',
            fontWeight: 600,
            transition: 'all 0.3s ease'
          }}>
            Back to Resources
          </button>
        </div>
      </div>
    );
  }

  const title = item.name || item.title;
  const description = item.description || item.bio || item.content;
  const imageUrl = item.imageUrl || item.logo || item.photo || item.avatar;
  const category = item.category || item.type || item.department;

  // Render features list
  const renderFeatures = (features) => {
    if (!features || features.length === 0) {
      return (
        <div style={{ marginBottom: '2rem' }}>
          <h3 style={{ color: '#0A192F', marginBottom: '1rem', fontSize: '1.2rem', fontWeight: 600 }}>
            <i className="fas fa-star" style={{ color: typeConfig.color, marginRight: '0.5rem' }}></i>
            Key Features
          </h3>
          <p style={{ color: '#94A3B8', textAlign: 'center', padding: '2rem' }}>No features listed.</p>
        </div>
      );
    }
    return (
      <div style={{ marginBottom: '2rem' }}>
        <h3 style={{ color: '#0A192F', marginBottom: '1rem', fontSize: '1.2rem', fontWeight: 600 }}>
          <i className="fas fa-star" style={{ color: typeConfig.color, marginRight: '0.5rem' }}></i>
          Key Features
        </h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '0.75rem' }}>
          {features.map((feature, idx) => (
            <div key={idx} style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.75rem',
              padding: '0.5rem',
              background: '#F7F9FC',
              borderRadius: '10px'
            }}>
              <i className="fas fa-check-circle" style={{ color: '#10B981', fontSize: '1rem' }}></i>
              <span style={{ color: '#5A6E7F', fontSize: '0.9rem' }}>{feature}</span>
            </div>
          ))}
        </div>
      </div>
    );
  };

  // Render application details
  const renderApplicationDetails = (item) => {
    const applicationDescription = item.applicationDescription || '';
    const applicationImageUrl = item.applicationImageUrl || '';
    
    if (!applicationDescription && !applicationImageUrl) {
      return (
        <div style={{ marginBottom: '2rem' }}>
          <h3 style={{ color: '#0A192F', marginBottom: '1rem', fontSize: '1.2rem', fontWeight: 600 }}>
            <i className="fas fa-rocket" style={{ color: typeConfig.color, marginRight: '0.5rem' }}></i>
            Applications
          </h3>
          <div style={{
            textAlign: 'center',
            padding: '3rem',
            background: '#F7F9FC',
            borderRadius: '12px'
          }}>
            <i className="fas fa-info-circle" style={{ fontSize: '2rem', color: '#CBD5E1', marginBottom: '1rem' }}></i>
            <p style={{ color: '#94A3B8' }}>No application details available for this product.</p>
          </div>
        </div>
      );
    }

    return (
      <div style={{ marginBottom: '2rem' }}>
        <h3 style={{ color: '#0A192F', marginBottom: '1rem', fontSize: '1.2rem', fontWeight: 600 }}>
          <i className="fas fa-rocket" style={{ color: typeConfig.color, marginRight: '0.5rem' }}></i>
          Applications
        </h3>
        
        {applicationImageUrl && (
          <div style={{
            marginBottom: '1.5rem',
            borderRadius: '16px',
            overflow: 'hidden',
            boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
          }}>
            <img 
              src={getFullImageUrl(applicationImageUrl)} 
              alt="Application"
              style={{ width: '100%', maxHeight: '300px', objectFit: 'cover' }}
              onError={(e) => {
                e.target.style.display = 'none';
              }}
            />
          </div>
        )}
        
        {applicationDescription && (
          <div style={{
            background: 'linear-gradient(135deg, #2C7A7B08 0%, #236B6C08 100%)',
            padding: '1.5rem',
            borderRadius: '12px',
            borderLeft: `4px solid ${typeConfig.color}`
          }}>
            <h4 style={{ 
              color: '#0A192F', 
              marginBottom: '0.75rem',
              fontSize: '0.95rem',
              fontWeight: 600,
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}>
              <i className="fas fa-file-alt" style={{ color: typeConfig.color }}></i>
              Application Description
            </h4>
            <p style={{ 
              color: '#5A6E7F', 
              lineHeight: '1.8',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word'
            }}>
              {applicationDescription}
            </p>
          </div>
        )}
      </div>
    );
  };

  const renderCurriculum = (curriculum) => {
    if (!curriculum || curriculum.length === 0) return null;
    return (
      <div style={{ marginBottom: '2rem' }}>
        <h3 style={{ color: '#0A192F', marginBottom: '1rem', fontSize: '1.2rem', fontWeight: 600 }}>
          <i className="fas fa-book" style={{ color: typeConfig.color, marginRight: '0.5rem' }}></i>
          Course Curriculum
        </h3>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          {curriculum.map((module, idx) => (
            <div key={idx} style={{
              display: 'flex',
              alignItems: 'center',
              gap: '1rem',
              padding: '0.75rem',
              background: '#F7F9FC',
              borderRadius: '10px',
              transition: 'all 0.3s ease'
            }}>
              <div style={{
                width: '30px',
                height: '30px',
                background: typeConfig.color,
                borderRadius: '8px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'white',
                fontWeight: 700,
                fontSize: '0.8rem'
              }}>
                {idx + 1}
              </div>
              <span style={{ color: '#5A6E7F', fontSize: '0.9rem', flex: 1 }}>{module}</span>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderAgenda = (agenda) => {
    if (!agenda || agenda.length === 0) return null;
    return (
      <div style={{ marginBottom: '2rem' }}>
        <h3 style={{ color: '#0A192F', marginBottom: '1rem', fontSize: '1.2rem', fontWeight: 600 }}>
          <i className="fas fa-clock" style={{ color: typeConfig.color, marginRight: '0.5rem' }}></i>
          Event Agenda
        </h3>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          {agenda.map((item, idx) => (
            <div key={idx} style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.75rem',
              padding: '0.75rem',
              background: '#F7F9FC',
              borderRadius: '10px'
            }}>
              <div style={{
                width: '8px',
                height: '8px',
                background: typeConfig.color,
                borderRadius: '50%'
              }} />
              <span style={{ color: '#5A6E7F', fontSize: '0.9rem' }}>{item}</span>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderSpeakers = (speakers) => {
    if (!speakers || speakers.length === 0) return null;
    return (
      <div style={{ marginBottom: '2rem' }}>
        <h3 style={{ color: '#0A192F', marginBottom: '1rem', fontSize: '1.2rem', fontWeight: 600 }}>
          <i className="fas fa-users" style={{ color: typeConfig.color, marginRight: '0.5rem' }}></i>
          Speakers
        </h3>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1rem' }}>
          {speakers.map((speaker, idx) => (
            <div key={idx} style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.75rem',
              padding: '0.5rem 1rem',
              background: '#F7F9FC',
              borderRadius: '30px'
            }}>
              <i className="fas fa-user-circle" style={{ color: typeConfig.color, fontSize: '1.2rem' }}></i>
              <span style={{ color: '#5A6E7F', fontSize: '0.9rem' }}>{speaker}</span>
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div style={{ padding: '2rem', background: '#F7F9FC', minHeight: '100vh' }}>
      <div className="container" style={{ maxWidth: '1200px', margin: '0 auto' }}>
        {/* Back Button */}
        <button
          onClick={handleBack}
          style={{
            marginBottom: '2rem',
            padding: '0.6rem 1.2rem',
            background: 'white',
            border: '1px solid #E2E8F0',
            borderRadius: '10px',
            cursor: 'pointer',
            display: 'inline-flex',
            alignItems: 'center',
            gap: '0.5rem',
            color: '#5A6E7F',
            transition: 'all 0.3s ease',
            fontSize: '0.9rem'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = '#2C7A7B';
            e.currentTarget.style.color = 'white';
            e.currentTarget.style.borderColor = '#2C7A7B';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = 'white';
            e.currentTarget.style.color = '#5A6E7F';
            e.currentTarget.style.borderColor = '#E2E8F0';
          }}
        >
          <i className="fas fa-arrow-left"></i> Back to Resources
        </button>

        {/* Main Content Card */}
        <div style={{
          background: 'white',
          borderRadius: '24px',
          overflow: 'hidden',
          boxShadow: '0 20px 40px rgba(0,0,0,0.1)'
        }}>
          {/* Hero Section with Large Image */}
          <div style={{
            position: 'relative',
            overflow: 'hidden'
          }}>
            {imageUrl ? (
              <>
                {/* Full Size Image */}
                <div 
                  style={{
                    width: '100%',
                    height: '500px',
                    background: '#0A192F',
                    position: 'relative',
                    cursor: 'pointer'
                  }}
                  onClick={() => setShowFullImage(true)}
                >
                  <img 
                    src={getFullImageUrl(imageUrl)} 
                    alt={title}
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'contain',
                      objectPosition: 'center',
                      background: 'linear-gradient(135deg, #0A192F 0%, #1E3A5F 100%)'
                    }}
                    onError={(e) => {
                      e.target.style.display = 'none';
                    }}
                  />
                  
                  {/* Image Overlay - Click to expand */}
                  <div style={{
                    position: 'absolute',
                    bottom: '0',
                    left: '0',
                    right: '0',
                    padding: '2rem',
                    background: 'linear-gradient(transparent, rgba(0,0,0,0.7))',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-end',
                    color: 'white'
                  }}>
                    <div>
                      {category && (
                        <div style={{
                          display: 'inline-block',
                          background: 'rgba(255,255,255,0.2)',
                          padding: '0.3rem 1rem',
                          borderRadius: '20px',
                          fontSize: '0.8rem',
                          marginBottom: '0.5rem'
                        }}>
                          {category}
                        </div>
                      )}
                      <h1 style={{ fontSize: 'clamp(1.5rem, 4vw, 2rem)', fontWeight: 700 }}>
                        {title}
                      </h1>
                      {item.position && (
                        <p style={{ fontSize: '1rem', opacity: 0.9 }}>{item.position}</p>
                      )}
                    </div>
                    {item.price && (
                      <div style={{
                        fontSize: '1.3rem',
                        fontWeight: 700,
                        background: 'rgba(44,122,123,0.9)',
                        padding: '0.5rem 1.5rem',
                        borderRadius: '30px',
                        backdropFilter: 'blur(10px)'
                      }}>
                        {item.price}
                      </div>
                    )}
                    <div style={{
                      position: 'absolute',
                      top: '1rem',
                      right: '1rem',
                      background: 'rgba(0,0,0,0.6)',
                      padding: '0.5rem 1rem',
                      borderRadius: '30px',
                      fontSize: '0.75rem',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                      backdropFilter: 'blur(10px)'
                    }}>
                      <i className="fas fa-expand"></i> Click to enlarge
                    </div>
                  </div>
                </div>
              </>
            ) : (
              <div style={{
                height: '300px',
                background: typeConfig.gradient,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexDirection: 'column',
                color: 'white',
                padding: '2rem'
              }}>
                <div style={{
                  fontSize: '4rem',
                  marginBottom: '1rem'
                }}>
                  {typeConfig.icon}
                </div>
                {category && (
                  <div style={{
                    display: 'inline-block',
                    background: 'rgba(255,255,255,0.2)',
                    padding: '0.3rem 1rem',
                    borderRadius: '20px',
                    fontSize: '0.8rem',
                    marginBottom: '0.5rem'
                  }}>
                    {category}
                  </div>
                )}
                <h1 style={{ fontSize: 'clamp(1.5rem, 4vw, 2rem)', fontWeight: 700, textAlign: 'center' }}>
                  {title}
                </h1>
                {item.position && (
                  <p style={{ fontSize: '1rem', opacity: 0.9 }}>{item.position}</p>
                )}
                {item.price && (
                  <div style={{
                    marginTop: '1rem',
                    fontSize: '1.3rem',
                    fontWeight: 700,
                    background: 'rgba(255,255,255,0.2)',
                    padding: '0.3rem 1rem',
                    borderRadius: '30px',
                    backdropFilter: 'blur(10px)'
                  }}>
                    {item.price}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Full Image Modal */}
          {showFullImage && imageUrl && (
            <div
              style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: 'rgba(0,0,0,0.95)',
                zIndex: 9999,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                padding: '2rem'
              }}
              onClick={() => setShowFullImage(false)}
            >
              <button
                style={{
                  position: 'absolute',
                  top: '1.5rem',
                  right: '2rem',
                  background: 'rgba(255,255,255,0.1)',
                  border: 'none',
                  color: 'white',
                  fontSize: '2rem',
                  cursor: 'pointer',
                  padding: '0.5rem 1rem',
                  borderRadius: '8px',
                  backdropFilter: 'blur(10px)',
                  transition: 'all 0.3s ease'
                }}
                onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.2)'}
                onMouseLeave={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.1)'}
                onClick={(e) => {
                  e.stopPropagation();
                  setShowFullImage(false);
                }}
              >
                ✕
              </button>
              <img 
                src={getFullImageUrl(imageUrl)} 
                alt={title}
                style={{
                  maxWidth: '95%',
                  maxHeight: '95%',
                  objectFit: 'contain',
                  borderRadius: '8px'
                }}
                onClick={(e) => e.stopPropagation()}
              />
              <div style={{
                position: 'absolute',
                bottom: '2rem',
                left: '50%',
                transform: 'translateX(-50%)',
                color: 'rgba(255,255,255,0.5)',
                fontSize: '0.8rem'
              }}>
                Click anywhere to close
              </div>
            </div>
          )}

          {/* Content Tabs */}
          <div style={{ padding: '2rem' }}>
            <div style={{
              display: 'flex',
              gap: '0.5rem',
              borderBottom: '2px solid #E2E8F0',
              marginBottom: '2rem',
              flexWrap: 'wrap'
            }}>
              {typeConfig.sections.map(section => (
                <button
                  key={section}
                  onClick={() => setActiveTab(section)}
                  style={{
                    padding: '0.6rem 1.2rem',
                    background: activeTab === section ? typeConfig.color : 'transparent',
                    color: activeTab === section ? 'white' : '#5A6E7F',
                    border: 'none',
                    borderRadius: '10px',
                    cursor: 'pointer',
                    fontWeight: 600,
                    transition: 'all 0.3s ease',
                    fontSize: '0.85rem'
                  }}
                >
                  {section.charAt(0).toUpperCase() + section.slice(1)}
                </button>
              ))}
            </div>

            {/* Overview Tab */}
            {activeTab === 'overview' && (
              <div>
                <h3 style={{ color: '#0A192F', marginBottom: '1rem', fontSize: '1.2rem', fontWeight: 600 }}>
                  <i className="fas fa-info-circle" style={{ color: typeConfig.color, marginRight: '0.5rem' }}></i>
                  Description
                </h3>
                <p style={{ color: '#5A6E7F', lineHeight: '1.8', marginBottom: '2rem' }}>
                  {description || 'No description available.'}
                </p>
                
                {item.startDate && (
                  <>
                    <h3 style={{ color: '#0A192F', marginBottom: '1rem', fontSize: '1.2rem', fontWeight: 600 }}>
                      <i className="fas fa-calendar-alt" style={{ color: typeConfig.color, marginRight: '0.5rem' }}></i>
                      Date & Time
                    </h3>
                    <div style={{ 
                      background: '#F7F9FC', 
                      padding: '1rem', 
                      borderRadius: '12px', 
                      marginBottom: '2rem',
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                      gap: '1rem'
                    }}>
                      <div>
                        <strong style={{ color: '#0A192F' }}>Start:</strong>
                        <p style={{ color: '#5A6E7F', marginTop: '0.25rem' }}>
                          {new Date(item.startDate).toLocaleDateString()} {item.startTime && `at ${item.startTime}`}
                        </p>
                      </div>
                      {item.endDate && (
                        <div>
                          <strong style={{ color: '#0A192F' }}>End:</strong>
                          <p style={{ color: '#5A6E7F', marginTop: '0.25rem' }}>
                            {new Date(item.endDate).toLocaleDateString()} {item.endTime && `at ${item.endTime}`}
                          </p>
                        </div>
                      )}
                      {item.location && (
                        <div>
                          <strong style={{ color: '#0A192F' }}>Location:</strong>
                          <p style={{ color: '#5A6E7F', marginTop: '0.25rem' }}>{item.location}</p>
                        </div>
                      )}
                      {item.isVirtual && (
                        <div>
                          <strong style={{ color: '#0A192F' }}>Format:</strong>
                          <p style={{ color: '#5A6E7F', marginTop: '0.25rem' }}>Virtual Event</p>
                        </div>
                      )}
                    </div>
                  </>
                )}
                
                {item.duration && (
                  <div style={{ 
                    background: '#F7F9FC', 
                    padding: '1rem', 
                    borderRadius: '12px', 
                    marginBottom: '2rem',
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                    gap: '1rem'
                  }}>
                    <div>
                      <strong style={{ color: '#0A192F' }}>Duration:</strong>
                      <p style={{ color: '#5A6E7F', marginTop: '0.25rem' }}>{item.duration}</p>
                    </div>
                    {item.level && (
                      <div>
                        <strong style={{ color: '#0A192F' }}>Level:</strong>
                        <p style={{ color: '#5A6E7F', marginTop: '0.25rem' }}>{item.level}</p>
                      </div>
                    )}
                    {item.seats && (
                      <div>
                        <strong style={{ color: '#0A192F' }}>Seats Available:</strong>
                        <p style={{ color: '#5A6E7F', marginTop: '0.25rem' }}>{item.seats}</p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Features Tab */}
            {activeTab === 'features' && renderFeatures(item.features)}
            {activeTab === 'curriculum' && renderCurriculum(item.curriculum)}
            {activeTab === 'agenda' && renderAgenda(item.agenda)}
            {activeTab === 'speakers' && renderSpeakers(item.speakers)}
            
            {/* Application Tab */}
            {activeTab === 'application' && renderApplicationDetails(item)}
            
            {/* Instructor Tab */}
            {activeTab === 'instructor' && item.instructor && (
              <div style={{ marginBottom: '2rem' }}>
                <h3 style={{ color: '#0A192F', marginBottom: '1rem', fontSize: '1.2rem', fontWeight: 600 }}>
                  <i className="fas fa-user-tie" style={{ color: typeConfig.color, marginRight: '0.5rem' }}></i>
                  Instructor
                </h3>
                <div style={{ 
                  background: '#F7F9FC', 
                  padding: '1.5rem', 
                  borderRadius: '16px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '1rem'
                }}>
                  <div style={{
                    width: '60px',
                    height: '60px',
                    background: typeConfig.color,
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '1.5rem',
                    color: 'white'
                  }}>
                    <i className="fas fa-chalkboard-user"></i>
                  </div>
                  <div>
                    <h4 style={{ color: '#0A192F', marginBottom: '0.25rem' }}>{item.instructor}</h4>
                    <p style={{ color: '#5A6E7F', fontSize: '0.85rem' }}>Lead Instructor</p>
                  </div>
                </div>
              </div>
            )}

            {/* Points Tab */}
            {activeTab === 'points' && item.points && (
              <div style={{ marginBottom: '2rem' }}>
                <h3 style={{ color: '#0A192F', marginBottom: '1rem', fontSize: '1.2rem', fontWeight: 600 }}>
                  <i className="fas fa-star" style={{ color: typeConfig.color, marginRight: '0.5rem' }}></i>
                  Key Points
                </h3>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '0.75rem' }}>
                  {item.points.map((point, idx) => (
                    <div key={idx} style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.75rem',
                      padding: '0.5rem',
                      background: '#F7F9FC',
                      borderRadius: '10px'
                    }}>
                      <i className="fas fa-check-circle" style={{ color: '#10B981' }}></i>
                      <span style={{ color: '#5A6E7F' }}>{point}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Pricing Tab */}
            {activeTab === 'pricing' && item.price && (
              <div style={{ marginBottom: '2rem' }}>
                <h3 style={{ color: '#0A192F', marginBottom: '1rem', fontSize: '1.2rem', fontWeight: 600 }}>
                  <i className="fas fa-tag" style={{ color: typeConfig.color, marginRight: '0.5rem' }}></i>
                  Pricing
                </h3>
                <div style={{ 
                  background: 'linear-gradient(135deg, #2C7A7B10 0%, #236B6C10 100%)',
                  padding: '2rem',
                  borderRadius: '16px',
                  textAlign: 'center'
                }}>
                  <div style={{ fontSize: '2rem', fontWeight: 700, color: typeConfig.color }}>{item.price}</div>
                  <p style={{ color: '#5A6E7F', marginTop: '0.5rem' }}>Contact us for bulk discounts and enterprise pricing</p>
                </div>
              </div>
            )}

            {/* Contact Tab */}
            {activeTab === 'contact' && (
              <div style={{ marginBottom: '2rem' }}>
                <h3 style={{ color: '#0A192F', marginBottom: '1rem', fontSize: '1.2rem', fontWeight: 600 }}>
                  <i className="fas fa-address-card" style={{ color: typeConfig.color, marginRight: '0.5rem' }}></i>
                  Contact Information
                </h3>
                <div style={{ 
                  background: '#F7F9FC', 
                  padding: '1.5rem', 
                  borderRadius: '16px',
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
                  gap: '1rem'
                }}>
                  {item.email && (
                    <div>
                      <strong style={{ color: '#0A192F' }}>Email:</strong>
                      <p style={{ marginTop: '0.25rem' }}>
                        <a href={`mailto:${item.email}`} style={{ color: typeConfig.color, textDecoration: 'none' }}>{item.email}</a>
                      </p>
                    </div>
                  )}
                  {item.linkedin && (
                    <div>
                      <strong style={{ color: '#0A192F' }}>LinkedIn:</strong>
                      <p style={{ marginTop: '0.25rem' }}>
                        <a href={item.linkedin} target="_blank" rel="noopener noreferrer" style={{ color: typeConfig.color, textDecoration: 'none' }}>View Profile</a>
                      </p>
                    </div>
                  )}
                  {item.twitter && (
                    <div>
                      <strong style={{ color: '#0A192F' }}>Twitter:</strong>
                      <p style={{ marginTop: '0.25rem' }}>
                        <a href={item.twitter} target="_blank" rel="noopener noreferrer" style={{ color: typeConfig.color, textDecoration: 'none' }}>Follow</a>
                      </p>
                    </div>
                  )}
                  {item.website && (
                    <div>
                      <strong style={{ color: '#0A192F' }}>Website:</strong>
                      <p style={{ marginTop: '0.25rem' }}>
                        <a href={item.website} target="_blank" rel="noopener noreferrer" style={{ color: typeConfig.color, textDecoration: 'none' }}>Visit Website</a>
                      </p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Action Buttons */}
            <div style={{ display: 'flex', gap: '1rem', marginTop: '2rem', paddingTop: '2rem', borderTop: '1px solid #E2E8F0', flexWrap: 'wrap' }}>
              <button
                onClick={handleInquiry}
                style={{
                  flex: 1,
                  padding: '0.8rem 1.5rem',
                  background: typeConfig.gradient,
                  color: 'white',
                  border: 'none',
                  borderRadius: '12px',
                  fontSize: '0.95rem',
                  fontWeight: 600,
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '0.5rem'
                }}
                onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
                onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
              >
                <i className="fas fa-envelope"></i>
                Request Information
              </button>
              <button
                onClick={handleBack}
                style={{
                  flex: 1,
                  padding: '0.8rem 1.5rem',
                  background: 'transparent',
                  color: typeConfig.color,
                  border: `2px solid ${typeConfig.color}`,
                  borderRadius: '12px',
                  fontSize: '0.95rem',
                  fontWeight: 600,
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '0.5rem'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = typeConfig.color;
                  e.currentTarget.style.color = 'white';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'transparent';
                  e.currentTarget.style.color = typeConfig.color;
                }}
              >
                <i className="fas fa-arrow-left"></i>
                Back to Resources
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AllDetails;